/**
 * AIRDRIE-CORRIDOR — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 8 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_AIRDRIE_CORRIDOR: Location[] = [
// ────────────────────────────────────────────────────── AIRDRIE DOWNTOWN ──
  {
    slug: "airdrie-downtown",
    name: "Airdrie Downtown",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.2927, lng: -114.014 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "Airdrie Downtown shares its airspace corridor with Airdrie, about 0 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From the air, downtown Airdrie still reads as the original townsite — Main Street and Edmonton Trail carry the old north–south alignment through a compact grid that the city's curving newer streets were built around.",
      consequence:
        "For a storefront or commercial listing here, that grid is the story — parking, access, and how a customer actually reaches the door — and none of it fits into a ground-level frame.",
      resolution:
        "A mid-altitude oblique down Main Street holds the building, its access and the surrounding block in one readable frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Main Street",
      "Edmonton Trail",
      "Nose Creek Regional Park",
      "Airdrie Farmers Market",
      "Plainsmen Arena",
    ],
    terrain:
      "Flat prairie townsite ground on a straight street grid, with the green line of Nose Creek Regional Park along its edge and level prairie in every other direction.",
    windNote:
      "Open chinook-belt prairie. Airdrie has no terrain shelter, and downtown's low buildings do little to break a west wind that arrives at full strength.",
    seasonalWindow:
      "Flat ground and a low skyline keep both ends of the day usable; low spring and autumn sun rakes across the grid and gives the old storefront blocks their strongest definition. Summer evenings run long.",
    bylawNote:
      "City of Airdrie park and pathway rules govern takeoff and landing on municipal land, including Nose Creek Regional Park; launches stage from private property with the owner's consent.",
    subjectMix: ["commercial-retail", "luxury-residential"],
    mobilisationNote:
      "About 30 minutes north of the Calgary base. Same-day scheduling is realistic once the airspace authorisation for the address is in hand.",
    faqs: [
      {
        question: "Can a drone be flown legally in downtown Airdrie?",
        answer:
          "Yes, with the right paperwork. Downtown sits in Airdrie's controlled-airspace corridor, about 6 km from the Airdrie aerodrome, so work here runs under an Advanced RPAS certificate with a NAV CANADA authorisation obtained wherever the site requires one. The exact site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What does aerial photography add for a Main Street business?",
        answer:
          "Context. A storefront photo shows a façade; an aerial frame shows how the block works — where customers park, how Main Street connects to the rest of the city, and how close the property sits to a draw like the Airdrie Farmers Market. For commercial and leasing material, that context is the argument.",
      },
    ],
    primaryKeywords: [
      "drone photography Airdrie Downtown",
      "aerial photography Airdrie Downtown",
      "Airdrie Downtown drone services",
    ],
    nearestLocations: ["luxstone", "kings-heights", "sagewood"],
  },

  // ─────────────────────────────────────────────────────────────── BAYSIDE ──
  {
    slug: "bayside",
    name: "Bayside",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.268, lng: -114.033 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Bayside sits in the same airspace corridor as Airdrie, about 3 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The airspace numbers at Bayside are easy to misread: the Airdrie aerodrome sits 7 km away and the community holds the city's southwest corner, yet the corridor over the canals is still planned as controlled airspace.",
      consequence:
        "An operator who treats the edge of the city as free air quotes a canal shoot they cannot legally fly — and the discovery usually comes after the date is set.",
      resolution:
        "Work here runs under an Advanced RPAS certificate with the authorisation handled as part of booking, so a canal-line orbit is confirmed before the date and delivered in the standard turnaround.",
    },
    landmarks: [
      "the Bayside canals",
      "Chinook Winds Regional Park",
      "Bayside Drive SW",
      "Canals Boulevard SW",
    ],
    terrain:
      "Level prairie re-engineered around water — the Bayside canals thread between the lots in cut channels, giving a flat community the shoreline geometry the surrounding grid does not have.",
    windNote:
      "Chinook-belt prairie with no shelter to speak of; the canals sit below grade but do nothing to slow a west wind, and the open ground toward Chinook Winds Regional Park gives it a clean run.",
    seasonalWindow:
      "Open water in the canals holds colour and reflection from ice-off to freeze-up, which is when canal-front frames are strongest; in winter the channels read as clean white lines through the rooftops. Long prairie light at both ends of a summer day.",
    bylawNote:
      "Launches on municipal land, including parks and the canal pathways, fall under City of Airdrie rules; the working default is takeoff and landing from the client's own property with consent.",
    subjectMix: ["lakefront", "luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 30 minutes from the Calgary base. Easy same-day work once authorisation is settled, and Coopers Crossing or Windsong can share the same trip.",
    faqs: [
      {
        question: "Does a drone shoot in Bayside need NAV CANADA authorisation?",
        answer:
          "Usually, yes. Bayside sits in the controlled-airspace corridor that covers Airdrie, with the Airdrie aerodrome about 7 km away. Flights are conducted under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site requires one, with the site and altitude confirmed against NAV Drone first.",
      },
      {
        question: "How do you photograph a canal-front home in Bayside?",
        answer:
          "From the water line. A boundary-line orbit flown along the canal keeps the home large in frame while the channel and the connecting waterway locate it — the things a fence-line ground photo cannot show. Golden-hour passes put low sun on the water, which is when canal frontage does its best selling.",
      },
    ],
    primaryKeywords: [
      "drone photography Bayside",
      "aerial photography Bayside",
      "Bayside drone services",
    ],
    nearestLocations: ["coopers-crossing", "sagewood", "windsong", "luxstone"],
  },

  // ────────────────────────────────────────────────────── COOPERS CROSSING ──
  {
    slug: "coopers-crossing",
    name: "Coopers Crossing",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.26, lng: -114.026 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "Coopers Crossing sits in the same airspace corridor as Airdrie, about 4 km away, where flights are planned as controlled airspace. Work here is conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The prairie west of Coopers Crossing runs flat to the horizon, so evening light arrives low and unbroken — the boulevard trees along Coopers Drive SW catch a full golden hour that nothing upwind interrupts.",
      consequence:
        "That final hour is when the community's tree-lined streets and larger lots look most like the premium they command — a shoot slotted at midday leaves the flattest, least persuasive version of the place on file.",
      resolution:
        "Listings here are scheduled toward the golden-hour hold, with the orbit planned so the low sun crosses the lot rather than sitting behind it, delivered in the standard turnaround.",
    },
    landmarks: [
      "Coopers Drive SW",
      "Cooper's Town Promenade",
      "Coopers Crossing School",
      "the Coopers Crossing pathway network",
    ],
    terrain:
      "Level prairie given structure by planting — boulevard trees, the pathway network and green corridors organise flat ground into one of Airdrie's most deliberately landscaped communities.",
    windNote:
      "Chinook country, open to the west. Maturing trees take some edge off surface gusts at street level, but the wind aloft crosses Airdrie at full prairie strength.",
    seasonalWindow:
      "The landscaping sets the calendar: full leaf gives Coopers its strongest colour and depth, autumn turns the boulevards for a short window, and winter reduces the community to rooflines and pathway lines. Evening light is the reliable asset year-round.",
    bylawNote:
      "City of Airdrie rules govern launching from parks, green corridors and pathways; shoots stage their takeoff and landing from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 30 minutes north of the Calgary base. Comfortable same-day range, and close enough to Windsong and Bayside that two shoots can share one mobilisation.",
    faqs: [
      {
        question: "Is Coopers Crossing under controlled airspace for drones?",
        answer:
          "It is planned that way, yes. Coopers Crossing lies in the same airspace corridor as the rest of Airdrie, about 6 km from the Airdrie aerodrome. Flights run under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one and every site checked against NAV Drone beforehand.",
      },
      {
        question: "When should a Coopers Crossing listing be photographed?",
        answer:
          "In the last hour of good light, in leaf. The flat western horizon gives the community a long, low golden hour, and the boulevard trees and pathway corridors are what separate Coopers Crossing from newer bare-ground communities — both read strongest in evening sun from late spring into fall.",
      },
      {
        question: "Can you shoot commercial property at Cooper's Town Promenade?",
        answer:
          "Yes. The Promenade's retail sits inside the same authorisation picture as the rest of the community, so the same booking process applies. Aerial frames earn their keep in leasing material here because they show parking, access and the rooftops the customer base lives under — context a ground camera cannot reach.",
      },
    ],
    primaryKeywords: [
      "drone photography Coopers Crossing",
      "aerial photography Coopers Crossing",
      "Coopers Crossing drone services",
    ],
    nearestLocations: ["windsong", "bayside", "airdrie-downtown", "balzac"],
  },

  // ───────────────────────────────────────────────────────── KINGS HEIGHTS ──
  {
    slug: "kings-heights",
    name: "Kings Heights",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.276, lng: -113.989 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Kings Heights shares its airspace corridor with Airdrie, about 2 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Kings Heights was built in one sustained push, and it shows from altitude — rooflines run to a consistent height and pitch, fencing is uniform, and the crescents coil off Kingsview Boulevard SE in a single unbroken pattern.",
      consequence:
        "That consistency is what buyers like about the community and what makes an individual listing disappear — from a standard high oblique, one address looks like fifty others.",
      resolution:
        "The answer is altitude discipline: a lower, closer orbit keeps the home dominant in frame while one locator — Kingsview Market or the boulevard — holds the edge of the shot, delivered in the standard turnaround.",
    },
    landmarks: [
      "Kings Heights Drive SE",
      "Kingsview Boulevard SE",
      "Yankee Valley Boulevard",
      "Kingsview Market",
      "Genesis Place Recreation Centre",
    ],
    terrain:
      "Level east-side prairie carrying a planned community's geometry — crescents and closes stepped off a straight boulevard spine, with open grain land beginning again at the eastern edge.",
    windNote:
      "Exposed chinook-belt prairie. Nothing on Airdrie's east side breaks the wind, and gusts that read as manageable in a treed Calgary neighbourhood arrive here at full strength.",
    seasonalWindow:
      "New-build streetscapes photograph best under low, raking light, and the open horizons give that at both ends of the day in spring and autumn. Fresh snow simplifies the rooflines into strong graphic pattern; mid-summer midday flattens everything.",
    bylawNote:
      "Municipal parks, playgrounds and pathway land in Kings Heights fall under City of Airdrie rules for takeoff and landing, so launches stage from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 30 minutes north of the Calgary base. Same-day is realistic, and Ravenswood next door can share the same mobilisation and the same light.",
    faqs: [
      {
        question: "How close is Kings Heights to the Airdrie aerodrome, and does it matter?",
        answer:
          "About 4 km — close enough that the corridor here is planned as controlled airspace. Flights in Kings Heights are conducted under an Advanced RPAS certificate, with a NAV CANADA RPAS authorisation obtained wherever the specific site requires one, and the site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "How do you make one Kings Heights home stand out from the air?",
        answer:
          "By flying closer and lower than the default. The community's rooflines and fencing are deliberately consistent, so a high wide frame turns a listing into pattern. A tighter orbit keeps the home large, then one locator — Kingsview Market, the boulevard or Genesis Place Recreation Centre — anchors where it sits.",
      },
    ],
    primaryKeywords: [
      "drone photography Kings Heights",
      "aerial photography Kings Heights",
      "Kings Heights drone services",
    ],
    nearestLocations: ["ravenswood", "airdrie-downtown", "balzac"],
  },

  // ────────────────────────────────────────────────────────────── LUXSTONE ──
  {
    slug: "luxstone",
    name: "Luxstone",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.281, lng: -114.025 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "About 1 km from Airdrie and in the same airspace corridor, Luxstone is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Luxstone keeps its open ground on the east — the community runs along the green line of Nose Creek Regional Park — so its best light is the morning kind, coming up over parkland instead of over a facing roofline.",
      consequence:
        "A park-backing listing booked for a standard evening slot shows its selling side in shadow — the low sun sits behind the house, and the park frontage that justifies the price goes dark.",
      resolution:
        "The shoot is scheduled against the lot, not the diary: park-facing frames fly in the morning window, street-facing frames in the evening, and both land in the standard turnaround.",
    },
    landmarks: [
      "Luxstone Boulevard SW",
      "Nose Creek Regional Park",
      "Ron Ebbesen Arena",
      "Yankee Valley Boulevard",
    ],
    terrain:
      "Level west-central residential ground with one open edge — the green corridor of Nose Creek Regional Park runs along the community's east side while streets close in the plain on every other.",
    windNote:
      "Chinook-belt town with no meaningful shelter; the shallow open ground along Nose Creek Regional Park does not slow a west wind, and gust spread matters more than average speed for flight windows.",
    seasonalWindow:
      "Morning is the dependable window — clean light coming up over the park — while long summer evenings serve the street side. The park edge greens early in spring and holds colour late, which is when park-backing frames earn the most.",
    bylawNote:
      "Nose Creek Regional Park and the other municipal green spaces around Luxstone are covered by City of Airdrie takeoff and landing rules, so launches stage from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 30 minutes from the Calgary base, and morning slots matter more here than most places — booking the early window is usually the difference between a lit park frontage and a shadowed one.",
    faqs: [
      {
        question: "What airspace applies over Luxstone?",
        answer:
          "The Airdrie corridor, planned as controlled airspace — Luxstone sits about 1 km out from Airdrie and roughly 7 km from the Airdrie aerodrome. Work runs under an Advanced RPAS certificate with a NAV CANADA authorisation obtained wherever the site requires one, and every site is confirmed against NAV Drone first.",
      },
      {
        question: "Can aerial photos show a Luxstone home backing onto Nose Creek Regional Park?",
        answer:
          "Yes — that is the frame worth booking. From altitude, one oblique holds the house, the fence line and the open park behind it, which is the relationship a ground camera loses at the fence. Morning light is scheduled for those frames so the park side of the property is lit, not backlit.",
      },
    ],
    primaryKeywords: [
      "drone photography Luxstone",
      "aerial photography Luxstone",
      "Luxstone drone services",
    ],
    nearestLocations: ["sagewood", "bayside", "airdrie-downtown", "windsong"],
  },

  // ──────────────────────────────────────────────────────────── RAVENSWOOD ──
  {
    slug: "ravenswood",
    name: "Ravenswood",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.27, lng: -113.986 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Ravenswood shares its airspace corridor with Airdrie, about 3 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "There is almost no natural relief at Ravenswood — the tallest ground near the community is built, the raised embankment of the 40th Avenue QE2 interchange on its approach.",
      consequence:
        "Flat ground gives an aerial frame no depth for free; composition has to be earned through altitude and angle, or a wide shot collapses into a plan view that could be anywhere.",
      resolution:
        "Each pass here is flown at a working altitude chosen for a reason — low obliques to give rooflines shadow and depth, a higher hold when the point is the street pattern itself — delivered in the standard turnaround.",
    },
    landmarks: [
      "Ravenswood View SE",
      "Ravenskirk Close Playground",
      "the 40th Avenue QE2 interchange",
      "Kingsview Market",
    ],
    terrain:
      "Prairie-flat new-build ground on Airdrie's southeast corner, its crescents and closes laid onto a level plane with open farmland resuming immediately beyond the last street.",
    windNote:
      "Open chinook-belt prairie with nothing upwind of it; Ravenswood takes wind exactly as the forecast delivers it, and a marginal forecast means a marginal flight window.",
    seasonalWindow:
      "Low-angle light does the work here — spring and autumn evenings stretch shadows across the rooflines and give the flat plane its depth. Overcast midday is the weakest condition the community offers; fresh snow is one of the strongest.",
    bylawNote:
      "City of Airdrie rules apply to takeoff and landing on parks, playgrounds and other municipal land in Ravenswood; the default is to stage the launch from the client's own property with consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential"],
    mobilisationNote:
      "About 30 minutes from the Calgary base, with the community sitting at the 40th Avenue QE2 interchange — often the first or last stop on a multi-site Airdrie run.",
    faqs: [
      {
        question: "Do drone flights over Ravenswood need to be authorised?",
        answer:
          "Wherever the site requires it, yes. Ravenswood lies in the corridor planned as controlled airspace around Airdrie, about 4 km from the Airdrie aerodrome. Flights run under an Advanced RPAS certificate with NAV CANADA authorisation obtained as needed, and the specific site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "What does an aerial frame show a Ravenswood buyer that street photos cannot?",
        answer:
          "Position. From altitude, one frame places a listing against the things that decide a commute and a weekend — the 40th Avenue QE2 interchange at the community's approach, Kingsview Market nearby, and the open prairie edge beyond the last street. Ground photography cannot hold any of that in one shot.",
      },
    ],
    primaryKeywords: [
      "drone photography Ravenswood",
      "aerial photography Ravenswood",
      "Ravenswood drone services",
    ],
    nearestLocations: ["kings-heights", "airdrie-downtown", "balzac"],
  },

  // ────────────────────────────────────────────────────────────── SAGEWOOD ──
  {
    slug: "sagewood",
    name: "Sagewood",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.288, lng: -114.033 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Sagewood shares its airspace corridor with Airdrie, about 1 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Such relief as Sagewood has is cut downward, not raised — the Canals waterway and the fairway contours of Woodside Golf Course sit below the level of the streets, invisible behind fence lines from the ground.",
      consequence:
        "The community's two best features are the two a ground camera cannot see; a listing that backs onto water or fairway photographs, at street level, like a listing that backs onto neither.",
      resolution:
        "A boundary-line orbit flown along the water or the course edge restores the missing geography — home, channel and fairway in one frame — delivered in the standard turnaround.",
    },
    landmarks: [
      "Sagewood Boulevard SW",
      "the Canals waterway",
      "Woodside Golf Course",
      "Nose Creek Regional Park",
    ],
    terrain:
      "Near-level ground on Airdrie's northwest side where the built plane is interrupted only by cut features — the Canals waterway threading between lots and the green fall of Woodside Golf Course.",
    windNote:
      "The chinook belt, unsheltered. Neither the shallow cut of the Canals waterway nor the golf course tree lines amount to real wind protection; flight windows are planned against Airdrie's own exposed-prairie conditions.",
    seasonalWindow:
      "The water and the fairways run the calendar — open water and green turf from ice-off to freeze-up give Sagewood frames their colour, autumn turns the course for a short window, and winter reduces the waterway to a clean white line through the rooftops.",
    bylawNote:
      "Takeoff and landing on Sagewood's municipal land — parks, pathways and the waterway edges — is governed by City of Airdrie rules, so launches stage from private property with the owner's permission.",
    subjectMix: ["lakefront", "luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 30 minutes north of the Calgary base on the city's northwest side — an easy pairing with Luxstone or Bayside, and with Crossfield if the run continues north.",
    faqs: [
      {
        question: "Is authorisation needed for a drone shoot in Sagewood?",
        answer:
          "Generally, yes — wherever the specific site calls for it. Sagewood sits in the same corridor as Airdrie, about 1 km out, planned as controlled airspace, with the Airdrie aerodrome roughly 7 km away. Work is flown under an Advanced RPAS certificate, and each site and altitude is confirmed against NAV Drone beforehand.",
      },
      {
        question: "Can you photograph a home backing onto Woodside Golf Course?",
        answer:
          "Yes, and without flying over play. An orbit along the property line keeps the house large while the fairway fills the background; if a frame truly requires crossing the course, it is agreed with the course first. Book in the green season — the fairway colour is most of the argument for the frontage.",
      },
    ],
    primaryKeywords: [
      "drone photography Sagewood",
      "aerial photography Sagewood",
      "Sagewood drone services",
    ],
    nearestLocations: ["luxstone", "bayside", "airdrie-downtown", "crossfield"],
  },

  // ────────────────────────────────────────────────────────────── WINDSONG ──
  {
    slug: "windsong",
    name: "Windsong",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.274, lng: -114.043 },
    tier: 2,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "About 3 km from Airdrie and in the same airspace corridor, Windsong is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Windsong's plan puts its green through the middle rather than around the edge — the Windsong pathway system threads between the blocks, and from the air those connections, not the streets, are what organise the community.",
      consequence:
        "At street level the pathways vanish behind fences, so the feature buyers with kids and dogs actually use is missing from every ground photo of a listing here.",
      resolution:
        "A mid-altitude oblique lines up the home, its nearest pathway link and the open ground at Chinook Winds Regional Park in one frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Windstone Road SW",
      "Windsong Heights School",
      "Chinook Winds Regional Park",
      "the Windsong pathway system",
      "24 Street SW",
    ],
    terrain:
      "Flat westside prairie built out as a walkable grid — compact lots, connecting pathways and school grounds on level ground, with open country continuing west from the community's last street.",
    windNote:
      "The name is honest. Windsong sits on Airdrie's exposed western flank in the chinook belt, first in line for a west wind, and surface gusts here are assessed on their own rather than read off a Calgary forecast.",
    seasonalWindow:
      "Long, unobstructed light at both ends of the day; low spring and autumn sun gives rooflines and pathway lines their sharpest definition, and fresh snow turns the pathway network into the clearest drawing the community makes all year.",
    bylawNote:
      "City of Airdrie rules govern drone takeoff and landing on municipal ground — the pathway system, school sites and parks included — so shoots launch from private property with the owner's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential"],
    mobilisationNote:
      "About 30 minutes from the Calgary base. The westside location pairs naturally with Coopers Crossing or Bayside, and two sites can share a single mobilisation once each address is cleared.",
    faqs: [
      {
        question: "Windsong is on the far side of Airdrie from the aerodrome — is authorisation still needed?",
        answer:
          "Distance helps less than it seems. The aerodrome sits about 8 km away, yet Windsong still lies in the corridor planned as controlled airspace. Flights run under an Advanced RPAS certificate, with a NAV CANADA authorisation obtained wherever the specific site requires one, and the site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What makes Windsong different to photograph from above?",
        answer:
          "The pathway system. Windsong is organised around connections that run through the blocks rather than around them, and they disappear behind fences at ground level. From altitude, a single frame ties a home to its pathway link, Windsong Heights School and the open green at Chinook Winds Regional Park.",
      },
    ],
    primaryKeywords: [
      "drone photography Windsong",
      "aerial photography Windsong",
      "Windsong drone services",
    ],
    nearestLocations: ["coopers-crossing", "bayside", "luxstone", "airdrie-downtown"],
  }
];
